export const TRIGGER_OPTIONS = [
  { value: 'EVENT-001', label: '加入 LINE 官方好友', hint: 'LINE follow 事件觸發' },
  { value: 'EVENT-002', label: '註冊會員', hint: '會員完成註冊時' },
  { value: 'EVENT-201', label: '到指定時間／進入步驟', hint: '可立即執行，或設定指定觸發時間' },
  { value: 'EVENT-202', label: '等待指定天數', hint: '等待天數後自動推進' },
  { value: 'EVENT-301', label: '購物滿一定金額', hint: '單筆訂單完成且達指定金額門檻' },
  { value: 'EVENT-303', label: '購買特定商品', hint: '訂單完成且符合指定商品條件（可選任一或全部）' },
  { value: 'EVENT-501', label: '完成問卷', hint: '填寫人完成指定問卷時。流程內的問卷連結不受問卷本身一人一次限制' },
  { value: 'EVENT-101', label: '貼上指定互動標籤', hint: '貼上指定互動標籤時。後台手動貼標與批次貼標不會觸發' },
  { value: 'EVENT-601', label: '預約完成', hint: '美髮預約成立時。後台代訂與顧客線上預約都會觸發。沒有會員或 LINE 身份的訪客不會進入流程' },
  { value: 'EVENT-602', label: '訂位完成', hint: '餐廳訂位成立時。後台代訂與顧客線上訂位都會觸發。沒有會員或 LINE 身份的訪客不會進入流程' },
  { value: 'EVENT-603', label: '用餐完畢', hint: '餐廳訂位狀態改為已用餐完畢時' }
];

export const ACTION_OPTIONS = [
  { value: 'ACTION-002', label: '貼標籤' },
  { value: 'ACTION-004', label: '派發點數' },
  { value: 'ACTION-005', label: '派發優惠券' },
  { value: 'ACTION-008', label: '發送 LINE 推播' },
  { value: 'ACTION-006', label: '觸發抽獎連結' },
  { value: 'ACTION-013', label: '派送問卷' },
  { value: 'ACTION-012', label: '派發 Wixtar 票券' },
  { value: 'ACTION-011', label: '標記活動完成' }
];

export const TRIGGER_LABELS = Object.fromEntries(TRIGGER_OPTIONS.map((o) => [o.value, o.label]));
export const ACTION_LABELS = {
  ...Object.fromEntries(ACTION_OPTIONS.map((o) => [o.value, o.label])),
  // 舊活動仍可能存在 ACTION-007，僅保留顯示標籤，不再提供新建選項
  'ACTION-007': '集章／簽到'
};

/** 與 api/src/services/marketing/playbooks/registry.js playbookName 對齊 */
export const PLAYBOOK_LABELS = {
  PLAYBOOK_WELCOME_FRIEND: '新好友歡迎行銷活動流程',
  PLAYBOOK_REGISTER_GIFT: '註冊禮包',
  PLAYBOOK_SPEND_REWARD: '滿額回饋',
  PLAYBOOK_WINBACK: '沉睡會員喚回',
  PLAYBOOK_SECOND_PURCHASE: '新客二購促進',
  PLAYBOOK_VIP_ENGAGE: 'VIP 會員互動',
  PLAYBOOK_SEASONAL: '節慶活動',
  PLAYBOOK_ENGAGEMENT_CHECKIN: '活躍養成簽到',
  PLAYBOOK_COUPON_ONLY: '單步發券',
  PLAYBOOK_PUSH_ONLY: '單步推播',
  PLAYBOOK_QUESTIONNAIRE: '問卷行銷',
  PLAYBOOK_SALON_BOOKING: '預約完成關懷',
  PLAYBOOK_RESERVATION_BOOKED: '訂位完成關懷',
  PLAYBOOK_DINING_COMPLETED: '用餐完畢回饋'
};

export function getPlaybookLabel(playbookCode) {
  if (!playbookCode) return '-';
  return PLAYBOOK_LABELS[playbookCode] || playbookCode;
}

export function summarizeStep(step) {
  if (!step) return '';
  const trigger = describeTriggerDetail(step);
  const actions = (step.actions || [])
    .map((a) => ACTION_LABELS[a.actionType] || a.actionType)
    .join(' → ');
  return `當「${trigger}」→ ${actions || '（無動作）'}`;
}

function findResourceName(list, id, nameKeys = ['templateName', 'name', 'configName']) {
  if (!id || !list?.length) return null;
  const item = list.find((x) => String(x.id) === String(id));
  if (!item) return null;
  for (const key of nameKeys) {
    if (item[key]) return item[key];
  }
  return null;
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('zh-TW', { hour12: false });
}

export function describeTriggerDetail(step) {
  if (!step?.triggerType) return '未設定';
  const cfg = step.triggerConfig || {};
  const base = TRIGGER_LABELS[step.triggerType] || step.triggerType;

  switch (step.triggerType) {
    case 'EVENT-202':
      return `${base}（${cfg.waitDays ?? 3} 天）`;
    case 'EVENT-201':
      if (cfg.runOnActivate === true) return `${base}（進入步驟時立即執行）`;
      if (Number(cfg.waitValue) > 0) {
        const unit = cfg.waitUnit === 'hours' ? '小時' : '天';
        return `${base}（進入步驟後等待 ${Number(cfg.waitValue)} ${unit}）`;
      }
      if (cfg.scheduledAt) return `${base}（${formatDateTime(cfg.scheduledAt)}）`;
      return `${base}（尚未指定時間）`;
    case 'EVENT-301': {
      const amount = cfg.amount ?? cfg.minAmount ?? 0;
      return `${base}（滿 ${Number(amount).toLocaleString('zh-TW')} 元）`;
    }
    case 'EVENT-501':
      return cfg.questionnaireId ? `${base}（已指定問卷）` : `${base}（尚未選擇問卷）`;
    case 'EVENT-101':
      return cfg.tagId ? `${base}（已指定標籤）` : `${base}（尚未選擇標籤）`;
    case 'EVENT-303': {
      const skuCount = Array.isArray(cfg.skus) ? cfg.skus.filter((sku) => String(sku || '').trim()).length : 0;
      const productCount = cfg.products?.length || cfg.variantIds?.length || cfg.productIds?.length || 0;
      const count = skuCount || productCount;
      const modeLabel = cfg.productMatchMode === 'all' ? '全部符合' : '任一符合';
      const unit = skuCount && !productCount ? '個商品代碼' : '項商品';
      return count ? `${base}（${count} ${unit}，${modeLabel}）` : `${base}（尚未選擇商品）`;
    }
    default:
      return base;
  }
}

export function describeConditionDetail(step) {
  const cg = step?.conditionGroup;
  if (!cg || typeof cg !== 'object') return null;

  const parts = [];
  if (cg.tagIds?.length) {
    if (cg.tagLogic === 'AT_LEAST') {
      const count = Number(cg.tagMatchCount) || 1;
      parts.push(`標籤 ${cg.tagIds.length} 個中至少符合 ${count} 個`);
    } else {
      parts.push(`標籤符合邏輯 ${cg.tagLogic || 'OR'}（${cg.tagIds.length} 個標籤）`);
    }
  }
  if (cg.memberTierIds?.length) {
    parts.push(`限定會員等級（${cg.memberTierIds.length} 種）`);
  }
  if (cg.minOrderAmount != null) {
    parts.push(`單筆訂單滿 ${Number(cg.minOrderAmount).toLocaleString('zh-TW')} 元`);
  }
  return parts.length ? parts.join('；') : null;
}

export function describeActionDetail(action, resources = {}) {
  if (!action?.actionType) return '未設定動作';

  const label = ACTION_LABELS[action.actionType] || action.actionType;
  const cfg = action.actionConfig || {};
  const resolvedId = cfg.resolvedBinding?.resourceId;

  switch (action.actionType) {
    case 'ACTION-001':
    case 'ACTION-002': {
      const count = cfg.tagIds?.length || (cfg.tagId ? 1 : 0);
      const tagLabel = action.actionType === 'ACTION-001' ? '貼標籤' : label;
      return count ? `${tagLabel}（${count} 個）` : `${tagLabel}（未選標籤）`;
    }
    case 'ACTION-004': {
      const pts = cfg.amount ?? cfg.points;
      return pts ? `${label}（${pts} 點）` : label;
    }
    case 'ACTION-005': {
      const id = cfg.templateId || cfg.couponTemplateId || resolvedId;
      const name = findResourceName(resources.couponTemplates, id);
      return name ? `${label}：${name}` : (id ? `${label}（已選範本）` : `${label}（未選範本）`);
    }
    case 'ACTION-008': {
      const id = cfg.lineMsgTemplateId || cfg.templateId || resolvedId;
      const name = findResourceName(resources.lineTemplates, id);
      return name ? `${label}：${name}` : (id ? `${label}（已選範本）` : `${label}（未選範本）`);
    }
    case 'ACTION-006': {
      const id = cfg.lotteryConfigId || cfg.templateId || resolvedId;
      const name = findResourceName(resources.lotteryConfigs, id, ['configName', 'name', 'templateName']);
      return name ? `${label}：${name}` : (id ? `${label}（已選設定）` : `${label}（未選設定）`);
    }
    case 'ACTION-013': {
      const id = cfg.questionnaireId || resolvedId;
      const name = findResourceName(resources.questionnaires, id, ['name']);
      return name ? `${label}：${name}` : (id ? `${label}（已選問卷）` : `${label}（未選問卷）`);
    }
    case 'ACTION-007': {
      const id = cfg.stampCampaignId || cfg.templateId || resolvedId;
      return id ? `${label}（集章活動）` : `${label}（未指定集章活動）`;
    }
    case 'ACTION-012': {
      const ids = cfg.wixtarTicketIds || [];
      if (!ids.length) return `${label}（未選票券）`;
      const names = ids.map((id) => {
        const found = (resources.wixtarTickets || []).find((ticket) => String(ticket.ticketId) === String(id));
        return found?.ticketName || id;
      });
      return `${label}：${names.join('、')}`;
    }
    case 'ACTION-009':
      return cfg.markCompleted ? '標記活動完成（舊版動作）' : '更新進度（舊版動作）';
    default:
      return label;
  }
}

export function buildFlowOverview(steps, resources = {}) {
  return (steps || [])
    .slice()
    .sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0))
    .map((step) => ({
      stepOrder: step.stepOrder,
      stepName: step.stepName || `步驟 ${step.stepOrder}`,
      stepKey: step.id || `local-${step.stepOrder}`,
      trigger: describeTriggerDetail(step),
      condition: describeConditionDetail(step),
      actions: (step.actions || []).map((action) => describeActionDetail(action, resources))
    }));
}

/** 推播成本顯示：小數點第一位（例如 57 人 × 0.2 元 = 11.4） */
export function formatPushCost(value, { withSymbol = true } = {}) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  const formatted = num.toLocaleString('zh-TW', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
  return withSymbol ? `$${formatted}` : formatted;
}
